import { classes } from "./classes.js";
import {
  BANK_BG_BRAND,
  BANK_BG_INK,
  BANK_BG_SURFACE,
  BANK_BORDER,
  BANK_BORDER_STRONG,
  BANK_FOCUS,
  BANK_FS_BODY,
  BANK_RADIUS_CTL,
  BANK_TEXT_2,
} from "./tokens.js";

/**
 * `F-0b`: a short list of exclusive choices, side by side (prototype `.seg3`): status filters, fee toggles.
 *
 * A labelled `group` of toggle buttons with `aria-pressed`, which is what the prototype draws and what a screen
 * reader announces as « pressed »: a `radiogroup` would promise arrow-key roving that this does not implement.
 * Pressing the chosen segment does nothing.
 */
export interface SegmentOption {
  readonly value: string;
  readonly label: string;
  readonly testId?: string;
}

export interface SegmentedControlProps {
  /** The group's accessible name, from the catalogue. */
  readonly label: string;
  readonly value: string;
  readonly options: readonly SegmentOption[];
  readonly onChange: (value: string) => void;
  /**
   * `F-0c`: the pressed segment's fill. `accent` (default) is the brand; `ink` is the text colour, for a choice that is a
   * decision and not a filter (the composer's « Financer / À la signature »).
   */
  readonly tone?: "accent" | "ink";
  readonly className?: string;
}

export function SegmentedControl({
  label,
  value,
  options,
  onChange,
  tone = "accent",
  className,
}: SegmentedControlProps): React.JSX.Element {
  return (
    <div
      role="group"
      aria-label={label}
      data-kit="segmented"
      data-tone={tone}
      className={classes(
        "inline-flex flex-wrap overflow-hidden border",
        BANK_BORDER_STRONG,
        BANK_RADIUS_CTL,
        className,
      )}
    >
      {options.map((option) => {
        const pressed = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            data-testid={option.testId}
            onClick={() => {
              if (!pressed) onChange(option.value);
            }}
            className={classes(
              "min-h-[40px] border-r px-[18px] font-semibold last:border-r-0",
              BANK_BORDER,
              BANK_FS_BODY,
              BANK_FOCUS,
              pressed
                ? classes(tone === "ink" ? BANK_BG_INK : BANK_BG_BRAND, "text-white")
                : classes(BANK_BG_SURFACE, BANK_TEXT_2),
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
